/**
 * Sortierbare Tabelle der Sektion „Anfos & Umsatz". Eigene Komponente statt `TableWidget`: die hängt
 * am globalen Datumsfilter (wirft ohne Datum) und malt einen festen weißen Grund — beides passt nicht
 * zu Kapitelseiten, die ihre Daten selbst laden und im Dunkelmodus lesbar sein müssen.
 *
 * Spalten tragen ihre Erklärung (`tip`) selbst; die Kopfzeile wird aus derselben Liste gebaut wie die
 * Zellen, damit beide nicht auseinanderlaufen. Die Summenzeile kommt fertig aus dem Backend.
 *
 * Seit dem P3-Review (Plan 2026-10-23) gibt es Zusatzfelder für Kapitel 3 — alle OPTIONAL, ohne sie
 * verhält sich die Tabelle wie vorher (P0–P2 setzen sie nicht): Einheit als zweite Kopfzeile, `hinweis`
 * am Spalten-Tooltip, unsichere Zellen (`unsicher` → grau und beim Sortieren am Ende), aufklappbare
 * Kindzeilen (`kinder`), Abschnitte am Ende (`abschnitt`) und eine feste erste Spalte.
 *
 * Stufe 0 des P1/P2-Umbaus (Plan 2026-10-23-p1-p2-kpi-review-umbau) — wieder nur Opt-ins, ohne sie wie vorher:
 * `maxHeight: null` (keine innere Bildlaufleiste), `kinderText` (klickbarer Text neben dem ▸, z. B. „3 Monate"),
 * `kopfKlebtSeite` (Kopf bleibt beim SEITEN-Scroll stehen), `namensEtikett` (Etikett am Namen, z. B. „jung"),
 * `vergleichsZeilen` (feste Zeilen oben im Stil „Vergleich", nicht sortiert, nie grau) und `kopfGruppe` je Spalte
 * (Gruppenkopf über mehreren Spalten, colSpan aus den SICHTBAREN Spalten).
 */
import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoTip from '../InfoTip';
import { VERGLEICH_FLAECHE, VergleichEtikett } from './kennzahlTeile';

/**
 * Gedämpfte Zeile (`muted`, z. B. laufende Woche): die GANZE Zelle samt Unterzeilen grau (Audit D14) — vorher nur die
 * Zelle selbst, Unterzeilen mit eigener Farbe (`text.secondary`) blieben dunkel. Dieselbe Regel wie `unsicher` je Zelle.
 */
const GEDAEMPFT = { '& td': { color: 'text.disabled' }, '& td *': { color: 'inherit !important' } } as const;

/** Was eine Zelle über ihre Umgebung wissen darf. */
export interface ZellKontext {
  /** Spalte, nach der gerade sortiert wird (nach dem Rückfall auf die Standardsortierung). */
  sortKey: string | null;
  /** Grund, warum der Wert der Sortierspalte in DIESER Zeile unsicher ist (z. B. „wenig Anfos"), sonst null. */
  sortGrund: string | null;
  /** 0 = Zeile, 1 = Kindzeile. */
  ebene: 0 | 1;
}

export interface Spalte<T> {
  key: string;
  label: string;
  tip?: string;
  /** Satz, der nur in DIESER Tabelle gilt; wird an den Tooltip angehängt (der geteilte Begriff bleibt unverändert). */
  hinweis?: string;
  /** Einheit als zweite, kleine Kopfzeile („je 1.000 Kontakte", „in %"). */
  einheit?: string;
  align?: 'left' | 'right';
  /** Zellinhalt. */
  render: (row: T, kontext: ZellKontext) => ReactNode;
  /** Sortierwert; ohne ihn ist die Spalte nicht sortierbar. */
  sortValue?: (row: T) => number | string | null;
  /**
   * Grund, warum der Wert dieser Zeile unsicher ist (zu wenige Fälle), sonst null. Unsichere Zellen stehen
   * grau — mit ihren Unterzeilen — und beim Sortieren nach dieser Spalte in BEIDEN Richtungen am Ende.
   */
  unsicher?: (row: T) => string | null;
  /** Inhalt der Summenzeile. */
  summary?: ReactNode;
  minWidth?: number;
  /** Nur für die Spaltenwahl (`useSpaltenwahl`): Menügruppe und ob die Spalte immer sichtbar ist. */
  gruppe?: string;
  pflicht?: boolean;
  /**
   * Nur für die Spaltenwahl: Schlüssel der Partnerspalte — diese Spalte ist nur sichtbar und wählbar, solange der
   * Partner sichtbar ist (P2-V3 § 5: „Anteil schon bezahlt" nur mit „Bisher bezahlt").
   */
  nurMit?: string;
  /** Gruppenkopf über dieser Spalte (z. B. „Webinar-Einladungen"); benachbarte Spalten derselben Gruppe teilen ihn. */
  kopfGruppe?: string;
}

/** Spalten → Beschreibung für Spaltenwahl und Menü (Label und Tooltip einmal, aus der Spalte). */
export const spaltenMeta = <T,>(spalten: readonly Spalte<T>[]) =>
  spalten.map((s) => ({
    key: s.key,
    label: s.label,
    tip: [s.tip, s.hinweis].filter(Boolean).join(' ') || undefined,
    gruppe: s.gruppe ?? 'Weitere',
    pflicht: s.pflicht,
    ...(s.nurMit ? { nurMit: s.nurMit } : {}),
  }));

interface Props<T> {
  rows: T[];
  columns: Spalte<T>[];
  rowKey: (row: T) => string;
  /** Standardsortierung. Fehlt die gewählte Sortierspalte (ausgeblendet), fällt die Tabelle hierauf zurück. */
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Zeile ausgrauen (z. B. unreif, nicht zulässig). */
  muted?: (row: T) => boolean;
  /** Ausgegraute Zeilen unabhängig von der Sortierung ans Ende (ein grauer Ausreißer soll nicht oben stehen). */
  mutedLast?: boolean;
  summaryLabel?: string;
  /** Ab 21 Zeilen der obersten Ebene scrollt die Tabelle in sich (Standard 640 px); `null` = nie (Opt-out, P1/P2). */
  maxHeight?: number | null;
  dense?: boolean;
  /** Engere Zellen (6 statt 8 px Rand) — für Telefonbreite, wenn sonst einige Pixel fehlen. */
  eng?: boolean;
  /** Kindzeilen einer Zeile (aufklappbar, eingerückt, nach derselben Spalte sortiert). */
  kinder?: (row: T) => T[] | undefined;
  /** Kindzeilen dieser Zeile immer zeigen, ohne Aufklapp-Knopf und in gelieferter Reihenfolge (z. B. eine Vergleichszeile). */
  kinderImmerOffen?: (row: T) => boolean;
  /** Anfangs aufgeklappte Zeilen (rowKey). */
  initialOffen?: string[];
  /**
   * Abschnitt einer Zeile (z. B. „Nicht mehr im Einsatz"); Abschnitte stehen nach den übrigen Zeilen, in Reihenfolge ihres
   * Auftretens. Leerer Titel '' = nur ans Ende, ohne Überschriftzeile (Restzeilen wie „Sonstige und ohne Kennung").
   */
  abschnitt?: (row: T) => string | null;
  /** Erste Spalte beim waagrechten Scrollen stehen lassen. */
  festeErsteSpalte?: boolean;
  /**
   * Klickbarer Text neben dem Aufklapp-Pfeil (z. B. `(r, offen) => \`${n} Monate\``) — heute schaltet sonst nur der
   * Chevron (P2-V3 Regel Z b: „▸ 3 Monate"). Ohne Kindzeilen oder mit `kinderImmerOffen` erscheint er nicht.
   */
  kinderText?: (row: T, offen: boolean) => ReactNode;
  /**
   * Spaltenkopf klebt am SEITEN-Scroll (Opt-in, P2-V3 § 5, P1-V3 § 2.3): ohne eigenen Scroll-Container, also ohne
   * waagrechtes Scrollen — nur einschalten, wo die sichtbaren Spalten in die Karte passen (Breite „breit"). Schliesst
   * `maxHeight` und `festeErsteSpalte` aus. `top`: Abstand vom oberen Fensterrand in px (Standard 0).
   */
  kopfKlebtSeite?: boolean | { top: number };
  /** Etikett hinter dem Inhalt der ersten Spalte (z. B. „jung" aus `r.is_young`, P2-V3 § 2.0/3), Zeilen und Kindzeilen. */
  namensEtikett?: (row: T) => ReactNode;
  /**
   * Feste Zeilen über den Daten (z. B. „Typischer Termin"), nicht sortiert, im Stil „Vergleich": helle Fläche und
   * Etikett, Schrift normal und NIE grau — Vergleichswerte sind nicht unsicher (P1-V3 § 2.0 Regel 3b).
   */
  vergleichsZeilen?: T[];
}

type Sortierung = { key: string; dir: 'asc' | 'desc' };

/** Deckender Grund der festen ersten Spalte; die Karte darunter ist durchsichtig (ChartCard). */
const festerGrund = (t: Theme) => t.palette.background.default;
const festerHover = (t: Theme) =>
  t.palette.mode === 'dark' ? 'linear-gradient(rgba(255,255,255,0.08), rgba(255,255,255,0.08))' : 'linear-gradient(rgba(0,0,0,0.04), rgba(0,0,0,0.04))';

function SortableTable<T>({
  rows,
  columns,
  rowKey,
  initialSort,
  muted,
  mutedLast = false,
  summaryLabel,
  maxHeight = 640,
  dense = true,
  eng = false,
  kinder,
  kinderImmerOffen,
  initialOffen,
  abschnitt,
  festeErsteSpalte = false,
  kinderText,
  kopfKlebtSeite,
  namensEtikett,
  vergleichsZeilen,
}: Props<T>) {
  const [gewaehlt, setGewaehlt] = useState<Sortierung | null>(initialSort ?? null);
  const [offen, setOffen] = useState<Set<string>>(() => new Set(initialOffen ?? []));
  const [gescrollt, setGescrollt] = useState(false);

  // Rückfall: Ist die gewählte Spalte ausgeblendet, gilt die Standardsortierung (sonst stünden die Zeilen
  // ungeordnet und ohne Pfeil da).
  const sort = useMemo<Sortierung | null>(() => {
    if (gewaehlt && columns.some((c) => c.key === gewaehlt.key)) return gewaehlt;
    if (initialSort && columns.some((c) => c.key === initialSort.key)) return initialSort;
    return null;
  }, [gewaehlt, initialSort, columns]);
  const sortSpalte = sort ? columns.find((c) => c.key === sort.key) : undefined;

  const ordne = useMemo(() => {
    const f = sortSpalte?.sortValue;
    const unsicher = sortSpalte?.unsicher;
    const dir = sort?.dir === 'asc' ? 1 : -1;
    return (liste: T[]): T[] => {
      let out = liste;
      if (f) {
        // Rang vor Wert: sichere Werte, dann unsichere (grau), dann leere — in beiden Richtungen.
        const rang = (r: T, x: number | string | null) => (x === null ? 2 : unsicher?.(r) ? 1 : 0);
        out = [...liste].sort((a, b) => {
          const x = f(a);
          const y = f(b);
          const d = rang(a, x) - rang(b, y);
          if (d !== 0) return d;
          if (x === null || y === null) return 0;
          if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
          return String(x).localeCompare(String(y), 'de') * dir;
        });
      }
      if (mutedLast && muted) out = [...out.filter((r) => !muted(r)), ...out.filter((r) => muted(r))];
      return out;
    };
  }, [sortSpalte, sort, muted, mutedLast]);

  /** Zeilen in Anzeigereihenfolge: erst ohne Abschnitt, dann je Abschnitt eine Überschrift und seine Zeilen. */
  const bloecke = useMemo(() => {
    const ohne: T[] = [];
    const je = new Map<string, T[]>();
    for (const r of rows) {
      const a = abschnitt?.(r) ?? null;
      if (a === null) {
        ohne.push(r);
      } else {
        const liste = je.get(a) ?? [];
        liste.push(r);
        je.set(a, liste);
      }
    }
    return [{ titel: null as string | null, zeilen: ordne(ohne) }, ...Array.from(je, ([titel, z]) => ({ titel, zeilen: ordne(z) }))];
  }, [rows, abschnitt, ordne]);

  // Erster Klick: Zahlen und Datumswerte absteigend (größte bzw. neueste zuerst), Text aufsteigend (A–Z) — vorher
  // sortierte „Publisher" zuerst Z–A.
  const ersteRichtung = (c: Spalte<T>): 'asc' | 'desc' => {
    const probe = rows.map((r) => c.sortValue?.(r) ?? null).find((v) => v !== null);
    return typeof probe === 'string' && !/^\d{4}-\d{2}-\d{2}/.test(probe) ? 'asc' : 'desc';
  };

  const hasSummary = columns.some((c) => c.summary !== undefined);
  const umschalten = (k: string) =>
    setOffen((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const zelle = (r: T, c: Spalte<T>, i: number, ebene: 0 | 1, knopf: ReactNode) => {
    const grund = c.unsicher?.(r) ?? null;
    const kontext: ZellKontext = { sortKey: sort?.key ?? null, sortGrund: sortSpalte?.unsicher?.(r) ?? null, ebene };
    return (
      <TableCell
        key={c.key}
        align={c.align ?? 'left'}
        sx={{
          ...(c.align === 'right' ? { whiteSpace: 'nowrap' } : {}),
          // Grau gilt auch für die Unterzeilen der Zelle („x von y", „N Anfos").
          ...(grund ? { color: 'text.disabled', '& *': { color: 'inherit !important' } } : {}),
        }}
      >
        {i === 0 && knopf}
        {c.render(r, kontext)}
        {i === 0 && namensEtikett?.(r)}
      </TableCell>
    );
  };

  const zeilen = (r: T): ReactNode[] => {
    const k = rowKey(r);
    const kids = kinder?.(r) ?? [];
    const immer = kids.length > 0 && (kinderImmerOffen?.(r) ?? false);
    const auf = immer || offen.has(k);
    const pfeil =
      kids.length > 0 && !immer ? (
        <IconButton
          size="small"
          aria-label={auf ? 'zuklappen' : 'aufklappen'}
          onClick={() => umschalten(k)}
          sx={{ p: 0.25, mr: 0.5, ml: -0.5, verticalAlign: 'middle' }}
        >
          {auf ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      ) : null;
    // Opt-in: derselbe Schalter zusätzlich als Text („3 Monate"); ohne `kinderText` genau der Pfeil wie vorher.
    const knopf =
      pfeil && kinderText ? (
        <>
          {pfeil}
          <Box
            component="button"
            type="button"
            aria-expanded={auf}
            onClick={() => umschalten(k)}
            sx={{ border: 0, background: 'none', p: 0, mr: 0.75, font: 'inherit', fontSize: 12, color: 'text.secondary', cursor: 'pointer', textDecoration: 'underline dotted', verticalAlign: 'middle' }}
          >
            {kinderText(r, auf)}
          </Box>
        </>
      ) : (
        pfeil
      );
    const out: ReactNode[] = [
      <TableRow key={k} hover sx={muted?.(r) ? GEDAEMPFT : undefined}>
        {columns.map((c, i) => zelle(r, c, i, 0, knopf))}
      </TableRow>,
    ];
    if (auf) {
      for (const kind of immer ? kids : ordne(kids)) {
        out.push(
          <TableRow
            key={`${k}/${rowKey(kind)}`}
            className="kind"
            hover
            sx={muted?.(kind) ? GEDAEMPFT : undefined}
          >
            {columns.map((c, i) => zelle(kind, c, i, 1, null))}
          </TableRow>,
        );
      }
    }
    return out;
  };

  const tipText = (c: Spalte<T>) => [c.tip, c.hinweis].filter(Boolean).join(' ');
  const kopf = (c: Spalte<T>) => {
    const tip = tipText(c);
    return (
      <>
        {c.label}
        {tip && <InfoTip text={tip} />}
        {c.einheit && (
          <Box component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'text.secondary', lineHeight: 1.2 }}>
            {c.einheit}
          </Box>
        )}
      </>
    );
  };

  // Gruppenkopf (Opt-in): benachbarte SICHTBARE Spalten derselben `kopfGruppe` teilen eine Zelle, Spalten ohne Gruppe
  // eine leere. Nur wenn mindestens eine Spalte eine Gruppe trägt — sonst kein zusätzlicher Kopf.
  const kopfGruppen = useMemo(() => {
    if (!columns.some((c) => c.kopfGruppe)) return null;
    const out: { titel: string | null; span: number; key: string }[] = [];
    for (const c of columns) {
      const g = c.kopfGruppe ?? null;
      const letzte = out[out.length - 1];
      if (letzte && letzte.titel === g) letzte.span += 1;
      else out.push({ titel: g, span: 1, key: c.key });
    }
    return out;
  }, [columns]);

  // `kopfKlebtSeite`: kein eigener Scroll-Container — dann klebt der (stickyHeader-)Kopf am Seiten-Scroll.
  const klebt = kopfKlebtSeite === undefined || kopfKlebtSeite === false ? null : { top: kopfKlebtSeite === true ? 0 : kopfKlebtSeite.top };
  const hoehe = maxHeight === null || klebt ? undefined : maxHeight;

  return (
    // Kurze Tabellen ohne eigene Bildlaufleiste — erst lange Listen scrollen in sich.
    <TableContainer
      sx={klebt ? { overflow: 'visible' } : { maxHeight: rows.length > 20 ? hoehe : undefined, overflowX: 'auto' }}
      onScroll={festeErsteSpalte && !klebt ? (e) => setGescrollt(e.currentTarget.scrollLeft > 0) : undefined}
    >
      <Table
        size={dense ? 'small' : 'medium'}
        stickyHeader
        sx={{
          '& th, & td': { px: eng ? 0.75 : { xs: 1, md: 1.5 }, borderColor: 'divider' },
          '& td': { fontVariantNumeric: 'tabular-nums' },
          // Kindzeilen eingerückt. Als Tabellenregel, weil `& th, & td` oben das `pl` einer einzelnen Zelle überstimmt
          // (gemessen 6 statt 24 px, Bedienprüfung 28.10.).
          '& tr.kind > td:first-of-type': { pl: eng ? 3 : 4 },
          '& th': { backgroundColor: 'background.default', fontWeight: 600, verticalAlign: 'bottom' },
          ...(klebt ? { '& thead tr.kopf-spalten th': { top: klebt.top, zIndex: 3 } } : {}),
          ...(festeErsteSpalte && !klebt
            ? {
                '& th:first-of-type, & td:first-of-type': {
                  position: 'sticky',
                  left: 0,
                  backgroundColor: festerGrund,
                  boxShadow: gescrollt ? '4px 0 6px -4px rgba(0,0,0,0.35)' : 'none',
                },
                '& td:first-of-type': { zIndex: 1 },
                '& th:first-of-type': { zIndex: 3 },
                '& tr:hover > td:first-of-type': { backgroundImage: festerHover },
              }
            : {}),
        }}
      >
        <TableHead>
          {kopfGruppen && (
            <TableRow>
              {kopfGruppen.map((g) => (
                // Nicht klebend: zwei klebende Kopfzeilen lägen übereinander (beide top 0) — es klebt nur der Spaltenkopf.
                <TableCell
                  key={`gruppe:${g.key}`}
                  colSpan={g.span}
                  align="center"
                  sx={{ position: 'static', fontSize: 12, pb: 0.25, borderBottom: g.titel ? '2px solid' : 'none', borderBottomColor: 'divider' }}
                >
                  {g.titel}
                </TableCell>
              ))}
            </TableRow>
          )}
          <TableRow className="kopf-spalten">
            {columns.map((c) => (
              // Köpfe dürfen umbrechen — sonst ist die Tabelle bei 1.280 px breiter als die Karte und die
              // rechten (oft entscheidenden) Spalten verschwinden hinter der Bildlaufleiste (Review 07.10.).
              <TableCell key={c.key} align={c.align ?? 'left'} sx={{ minWidth: c.minWidth, whiteSpace: 'normal', lineHeight: 1.3 }}>
                {/* Info-Symbol direkt am Text: MUI hält rechts daneben Platz für den (unsichtbaren)
                    Sortierpfeil frei — dahinter gesetzt wirkte das Symbol wie das der Nachbarspalte. */}
                {c.sortValue ? (
                  <TableSortLabel
                    active={sort?.key === c.key}
                    // Eng (390 px): der unsichtbare Pfeil hielt je Spalte ~26 px frei, die Blog-Tabelle lief
                    // damit 3 px über (gemessen 20.10.); der aktive Pfeil bleibt sichtbar.
                    hideSortIcon={eng}
                    direction={sort?.key === c.key ? sort.dir : 'desc'}
                    onClick={() =>
                      setGewaehlt(
                        sort?.key === c.key ? { key: c.key, dir: sort.dir === 'desc' ? 'asc' : 'desc' } : { key: c.key, dir: ersteRichtung(c) },
                      )
                    }
                  >
                    {/* Ein Kind: rechtsbündig dreht TableSortLabel die Reihenfolge seiner Kinder um. */}
                    <Box component="span">{kopf(c)}</Box>
                  </TableSortLabel>
                ) : (
                  // Ohne Einheit wie vor dem Umbau (inline-flex zentriert das Info-Symbol); mit Einheit untereinander.
                  <Box sx={{ display: c.einheit ? 'inline-block' : 'inline-flex', alignItems: 'center' }}>{kopf(c)}</Box>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {vergleichsZeilen?.map((r) => (
            // Stil „Vergleich" (Regel 3b): helle Fläche, Etikett, normale Schrift; `unsicher` greift hier bewusst nicht.
            <TableRow key={`vergleich:${rowKey(r)}`} sx={{ '& td': { backgroundColor: VERGLEICH_FLAECHE } }}>
              {columns.map((c, i) => (
                <TableCell key={c.key} align={c.align ?? 'left'} sx={c.align === 'right' ? { whiteSpace: 'nowrap' } : undefined}>
                  {i === 0 && <VergleichEtikett />}
                  {c.render(r, { sortKey: sort?.key ?? null, sortGrund: null, ebene: 0 })}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {bloecke.flatMap((b) => [
            ...(b.titel && b.zeilen.length > 0
              ? [
                  <TableRow key={`abschnitt:${b.titel}`}>
                    <TableCell colSpan={columns.length} sx={{ fontStyle: 'italic', fontWeight: 600, color: 'text.secondary', pt: 1.5 }}>
                      {b.titel}
                    </TableCell>
                  </TableRow>,
                ]
              : []),
            ...b.zeilen.flatMap(zeilen),
          ])}
          {hasSummary && (
            <TableRow sx={{ '& td': { fontWeight: 600, borderTop: '2px solid', borderTopColor: 'divider' } }}>
              {columns.map((c, i) => (
                // Rechtsbündige Summen umbrechen nicht — wie die Datenzellen (P5-Review #25: „100,0 / %" bei 1.024 px).
                <TableCell key={c.key} align={c.align ?? 'left'} sx={c.align === 'right' ? { whiteSpace: 'nowrap' } : undefined}>
                  {c.summary ?? (i === 0 ? summaryLabel ?? 'Insgesamt' : null)}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SortableTable;
